import { type CodigoErroHermes, erroApiSchema, tokensResponseSchema } from '@varga/shared';
import {
  atualizarAccess,
  getAccessToken,
  getRefreshToken,
  limparSessao,
  type UsuarioSessao,
} from './session';

/**
 * Cliente HTTP do painel. Um `fetch` wrapper com:
 * - Authorization: Bearer <access em memória>
 * - refresh silencioso no 401 (`POST /v1/auth/refresh`), single-flight, re-tenta
 *   a requisição original uma vez;
 * - refresh falho → limpa a sessão e dispara `aoExpirarSessao` (o App leva ao
 *   login preservando rota + rascunho — UX-PAINEL-INTERNO §0.7 / §1).
 * Toda resposta de erro da API segue `{ erro: { codigo, mensagem, detalhes? } }`.
 */

export const API_URL: string = (import.meta.env.VITE_API_URL ?? 'http://localhost:3000').replace(
  /\/$/,
  '',
);

export class ApiError extends Error {
  readonly codigo: CodigoErroHermes | 'rede' | 'desconhecido';
  readonly status: number;
  readonly detalhes: unknown;
  constructor(
    codigo: ApiError['codigo'],
    mensagem: string,
    status: number,
    detalhes: unknown = undefined,
  ) {
    super(mensagem);
    this.name = 'ApiError';
    this.codigo = codigo;
    this.status = status;
    this.detalhes = detalhes;
  }
  get ehRede(): boolean {
    return this.codigo === 'rede';
  }
}

type Handler = () => void;
let aoExpirarSessao: Handler = () => {};
export function registrarExpiracaoSessao(h: Handler): void {
  aoExpirarSessao = h;
}

let refreshEmAndamento: Promise<boolean> | null = null;

async function tentarRefresh(): Promise<boolean> {
  if (refreshEmAndamento) return refreshEmAndamento;
  refreshEmAndamento = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;
    try {
      const resp = await fetch(`${API_URL}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!resp.ok) return false;
      const dados = tokensResponseSchema.parse(await resp.json());
      const u: UsuarioSessao = {
        id: dados.usuario.id,
        role: dados.usuario.role,
        clienteId: dados.usuario.clienteId,
        nome: dados.usuario.nome,
      };
      atualizarAccess(dados.accessToken, dados.refreshToken, u);
      return true;
    } catch {
      return false;
    } finally {
      refreshEmAndamento = null;
    }
  })();
  return refreshEmAndamento;
}

interface OpcoesRequisicao {
  method?: string;
  body?: unknown;
  /** por padrão a rota exige sessão; `false` para login/refresh/etc. */
  autenticada?: boolean;
  /** não tentar refresh no 401 (evita loop nas rotas de auth) */
  semRefresh?: boolean;
  signal?: AbortSignal;
}

async function requisicao(caminho: string, opts: OpcoesRequisicao = {}): Promise<unknown> {
  const { method = 'GET', body, autenticada = true, semRefresh = false, signal } = opts;

  const executar = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['content-type'] = 'application/json';
    const access = getAccessToken();
    if (autenticada && access) headers['authorization'] = `Bearer ${access}`;
    return fetch(`${API_URL}${caminho}`, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  };

  let resp: Response;
  try {
    resp = await executar();
  } catch {
    throw new ApiError('rede', 'Falha de rede', 0);
  }

  if (resp.status === 401 && autenticada && !semRefresh) {
    const ok = await tentarRefresh();
    if (ok) {
      try {
        resp = await executar();
      } catch {
        throw new ApiError('rede', 'Falha de rede', 0);
      }
    } else {
      limparSessao();
      aoExpirarSessao();
      throw new ApiError('nao_autenticado', 'Sessão expirada', 401);
    }
  }

  if (resp.status === 204) return undefined;

  let json: unknown = undefined;
  const texto = await resp.text();
  if (texto) {
    try {
      json = JSON.parse(texto);
    } catch {
      json = undefined;
    }
  }

  if (!resp.ok) {
    const parsed = erroApiSchema.safeParse(json);
    if (parsed.success) {
      throw new ApiError(
        parsed.data.erro.codigo,
        parsed.data.erro.mensagem,
        resp.status,
        parsed.data.erro.detalhes,
      );
    }
    throw new ApiError('desconhecido', `HTTP ${resp.status}`, resp.status);
  }

  return json;
}

/** GET/POST/etc. tipado: valida a resposta com um schema Zod de `@varga/shared`. */
export async function api<T>(
  schema: { parse: (v: unknown) => T },
  caminho: string,
  opts: OpcoesRequisicao = {},
): Promise<T> {
  const json = await requisicao(caminho, opts);
  return schema.parse(json);
}

/** Para respostas sem corpo (204) ou quando o corpo é irrelevante. */
export async function apiVoid(caminho: string, opts: OpcoesRequisicao = {}): Promise<void> {
  await requisicao(caminho, opts);
}

export type { OpcoesRequisicao };
